import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase';
import {
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Search
} from 'lucide-react';

const MyBetsHistory = () => {
  const [bets, setBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [betsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalBets: 0,
    totalWon: 0,
    totalLost: 0,
    totalRefunded: 0,
    totalWagered: 0,
    totalPayout: 0,
    netProfit: 0,
    winRate: 0,
    avgBetSize: 0,
    biggestWin: 0,
    currentStreak: 0
  });

  useEffect(() => {
    fetchBettingHistory();
  }, []);

  useEffect(() => {
    filterAndSortBets();
  }, [bets, filterStatus, sortBy, sortOrder, searchTerm]);

  const filterAndSortBets = () => {
    let filtered = [...bets];

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(bet => bet.status === filterStatus);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(bet =>
        bet.teams?.home?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bet.teams?.away?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bet.poolId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'date':
          compareValue = (a.placedAt || 0) - (b.placedAt || 0);
          break;
        case 'amount':
          compareValue = (a.bet?.amount || 0) - (b.bet?.amount || 0);
          break;
        case 'payout':
          const aPayoutPotential = a.payoutAmount || a.bet?.potentialPayout || 0;
          const bPayoutPotential = b.payoutAmount || b.bet?.potentialPayout || 0;
          compareValue = aPayoutPotential - bPayoutPotential;
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    setFilteredBets(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const fetchBettingHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated first
      if (!auth.currentUser) {
        setError('Please sign in to view your betting history');
        setLoading(false);
        return;
      }

      const getUserBets = httpsCallable(functions, 'getUserBets');

      const result = await getUserBets({ limit: 100 });

      if (result.data && result.data.bets) {
        setBets(result.data.bets);
        calculateStats(result.data.bets);
      } else {
        // No bets yet
        setBets([]);
        calculateStats([]);
      }
    } catch (err) {
      console.error('Error fetching betting history:', err);
      // More specific error messages
      if (err.code === 'unauthenticated') {
        setError('Please sign in to view your betting history');
      } else if (err.code === 'permission-denied') {
        setError('You do not have permission to view this data');
      } else {
        setError('Failed to load betting history. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (betsData) => {
    const stats = {
      totalBets: betsData.length,
      totalWon: 0,
      totalLost: 0,
      totalRefunded: 0,
      totalWagered: 0,
      totalPayout: 0,
      netProfit: 0,
      winRate: 0
    };

    let currentStreak = 0;
    let streakType = null;

    betsData.forEach(betData => {
      // Handle nested structure: bet.amount, bet.team, etc.
      const betAmount = betData.bet?.amount || 0;
      stats.totalWagered += betAmount;

      if (betData.status === 'won') {
        stats.totalWon++;
        const payoutAmount = betData.payoutAmount || betData.bet?.potentialPayout || 0;
        stats.totalPayout += payoutAmount;

        // Track biggest win
        const profit = payoutAmount - betAmount;
        if (profit > stats.biggestWin) {
          stats.biggestWin = profit;
        }

        // Track streak
        if (streakType === 'win' || !streakType) {
          currentStreak = streakType === 'win' ? currentStreak + 1 : 1;
          streakType = 'win';
        } else {
          currentStreak = 1;
          streakType = 'win';
        }
      } else if (betData.status === 'lost') {
        stats.totalLost++;

        // Track streak
        if (streakType === 'loss' || !streakType) {
          currentStreak = streakType === 'loss' ? currentStreak - 1 : -1;
          streakType = 'loss';
        } else {
          currentStreak = -1;
          streakType = 'loss';
        }
      } else if (betData.status === 'refunded') {
        stats.totalRefunded++;
        stats.totalPayout += betAmount; // Refunds return the original amount
      }
    });

    stats.currentStreak = currentStreak;
    stats.netProfit = stats.totalPayout - stats.totalWagered;
    stats.winRate = stats.totalBets > 0
      ? Math.round((stats.totalWon / (stats.totalWon + stats.totalLost)) * 100) || 0
      : 0;
    stats.avgBetSize = stats.totalBets > 0
      ? Math.round(stats.totalWagered / stats.totalBets)
      : 0;

    setStats(stats);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'won':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'lost':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'refunded':
        return <RefreshCw className="w-4 h-4 text-gray-400" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  // Pagination calculations
  const indexOfLastBet = currentPage * betsPerPage;
  const indexOfFirstBet = indexOfLastBet - betsPerPage;
  const currentBets = filteredBets.slice(indexOfFirstBet, indexOfLastBet);
  const totalPages = Math.ceil(filteredBets.length / betsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="space-y-4 text-white">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Betting History</h2>
            <p className="text-gray-400 text-sm mt-1">Track your performance and analyze results</p>
          </div>
          {stats.currentStreak !== 0 && (
            <div className={`px-3 py-1 rounded text-sm font-medium ${
              stats.currentStreak > 0
                ? 'bg-emerald-900 text-emerald-400'
                : 'bg-red-900 text-red-400'
            }`}>
              {Math.abs(stats.currentStreak)} Streak
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Bets</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.totalBets}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Win Rate</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.winRate}%</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Net Profit</p>
          <p className={`text-2xl font-bold mt-1 ${stats.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {stats.netProfit >= 0 ? '+' : ''}{stats.netProfit}
          </p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Wagered</p>
          <p className="text-2xl font-bold text-violet-400 mt-1">{stats.totalWagered}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Avg Bet</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.avgBetSize}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Best Win</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">+{stats.biggestWin}</p>
        </div>
      </div>

      {/* Win/Loss/Refund Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-800 rounded-lg p-4 text-center border border-emerald-900">
          <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-emerald-400">{stats.totalWon}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Wins</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 text-center border border-red-900">
          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-red-400">{stats.totalLost}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Losses</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
          <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-400">{stats.totalRefunded}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Refunded</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search teams or pools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:outline-none focus:border-violet-500 transition-colors"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="refunded">Refunded</option>
          </select>

          {/* Sort Options */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:outline-none focus:border-violet-500 transition-colors"
          >
            <option value="date">Sort by Date</option>
            <option value="amount">Sort by Amount</option>
            <option value="payout">Sort by Payout</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-gray-400 hover:text-white hover:border-violet-500 transition-colors"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Betting History Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">History</h3>
            <span className="text-xs text-gray-500">
              {indexOfFirstBet + 1}-{Math.min(indexOfLastBet, filteredBets.length)} of {filteredBets.length}
            </span>
          </div>
        </div>

        {filteredBets.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-400">
              {bets.length === 0 ? "No bets placed yet" : "No bets match your filters"}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {bets.length === 0
                ? "Start betting to see your history here"
                : "Try adjusting your filters"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-normal">Game</th>
                  <th className="px-4 py-3 text-left font-normal">Selection</th>
                  <th className="px-4 py-3 text-center font-normal">Amount</th>
                  <th className="px-4 py-3 text-center font-normal">Status</th>
                  <th className="px-4 py-3 text-center font-normal">Payout</th>
                  <th className="px-4 py-3 text-center font-normal">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {currentBets.map((betData, index) => (
                    <tr
                      key={betData.id || index}
                      className="border-t border-gray-700 hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-white">
                            {betData.teams?.home || 'N/A'} vs {betData.teams?.away || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {betData.poolId || betData.gameId}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${
                          betData.bet?.team === 'home'
                            ? 'text-violet-400'
                            : 'text-emerald-400'
                        }`}>
                          {betData.bet?.team === 'home' ? betData.teams?.home : betData.teams?.away}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-white">
                          {betData.bet?.amount || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <span className={`text-xs uppercase ${
                            betData.status === 'won' ? 'text-emerald-400' :
                            betData.status === 'lost' ? 'text-red-400' :
                            betData.status === 'refunded' ? 'text-gray-400' :
                            'text-gray-500'
                          }`}>
                            {betData.status || 'pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {betData.status === 'won' && (
                          <span className="text-sm text-emerald-400">
                            +{betData.payoutAmount || betData.bet?.potentialPayout || 0}
                          </span>
                        )}
                        {betData.status === 'lost' && (
                          <span className="text-sm text-red-400">
                            -{betData.bet?.amount || 0}
                          </span>
                        )}
                        {betData.status === 'refunded' && (
                          <span className="text-sm text-gray-400">
                            {betData.bet?.amount || 0}
                          </span>
                        )}
                        {(betData.status === 'pending' || !betData.status) && (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-gray-500">
                          {formatDate(betData.placedAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-gray-800 rounded-lg border border-gray-700 px-4 py-3">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-400 bg-gray-900 border border-gray-700 rounded hover:text-white hover:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => paginate(pageNum)}
                  className={`px-2 py-1 rounded text-sm transition-colors ${
                    currentPage === pageNum
                      ? 'bg-violet-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-400 bg-gray-900 border border-gray-700 rounded hover:text-white hover:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={fetchBettingHistory}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-gray-400 border border-gray-700 rounded hover:text-white hover:border-violet-500 transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  );
};

export default MyBetsHistory;